import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, Printer } from "lucide-react";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useShortlist } from "@/context/ShortlistContext";
import { STYLES, itemFinishes } from "@/components/FilterRow";
import { PrintInterlude, projectShots } from "@/components/PrintInterlude";
import { PrintBackCover } from "@/components/PrintBackCover";
import { useSheetZoom } from "@/lib/useSheetZoom";

const groupBySupplier = (items) => {
  const order = [...new Set(items.map((i) => i.supplier || ""))];
  return order.map((sup) => ({ sup, items: items.filter((i) => (i.supplier || "") === sup) }));
};

const PrintCard = ({ item }) => (
  <article className="print-card border border-[#d9cfb4] p-3 flex flex-col">
    <div className="aspect-[4/3] overflow-hidden bg-[#eee8da]">
      {item.images?.[0] && <img src={imgUrl(item.images[0])} alt={item.name} className="w-full h-full object-cover" />}
    </div>
    <div className="mt-3 flex justify-between items-baseline gap-2">
      <h3 className="font-display text-[19px] leading-tight text-[#14130f]">{item.name}</h3>
      <span className="font-mono-j text-[9px] tracking-[0.15em] text-[#9a7b26] shrink-0">{item.code}</span>
    </div>
    {item.supplier && <p className="text-[10px] text-[#5d5a52] mt-0.5">{item.supplier}</p>}
    {asList(item.specs).length > 0 && (
      <dl className="mt-2 text-[9.5px] leading-snug text-[#3b3933]">
        {item.specs.slice(0, 4).map((s, i) => (
          <div key={i} className="flex justify-between border-t border-[#e6dfcc] py-0.5"><dt className="uppercase tracking-wider text-[#8a857a]">{s.label}</dt><dd>{s.value}</dd></div>
        ))}
      </dl>
    )}
  </article>
);

const Cover = ({ sections, single, selection, filters }) => (
  <section className="sheet cover flex flex-col justify-between">
    <div className="flex justify-between items-start text-[#9a7b26]">
      <LogoMark variant="dark" className="h-20 w-auto" />
      <span className="font-mono-j text-[10px] tracking-[0.3em] uppercase">Vol. {new Date().getFullYear()}</span>
    </div>
    <div>
      <p className="font-mono-j text-[10px] tracking-[0.35em] uppercase text-[#9a7b26]">Sterling Joinery</p>
      <h1 className="font-display font-light text-[64px] leading-[0.95] text-[#14130f] mt-4">
        {selection ? <>Material<br /><em>Selection</em></> : single ? sections[0]?.cat.name : <>The Atlas<br /><em>Catalogue</em></>}
      </h1>
      <div className="mt-6 h-px w-24 bg-[#9a7b26]" />
      {filters && <p className="mt-5 font-mono-j text-[11px] tracking-[0.25em] uppercase text-[#3b3933]" data-testid="print-cover-filters">{filters}</p>}
      {selection && (selection.client || selection.project) && (
        <div className="mt-6 text-[13px] text-[#3b3933] space-y-1">
          {selection.client && <p><span className="font-mono-j text-[9px] tracking-[0.25em] uppercase text-[#8a857a] mr-3">Prepared for</span>{selection.client}</p>}
          {selection.project && <p><span className="font-mono-j text-[9px] tracking-[0.25em] uppercase text-[#8a857a] mr-3">Project</span>{selection.project}</p>}
        </div>
      )}
    </div>
    <div>
      <p className="font-mono-j text-[10px] tracking-[0.3em] uppercase text-[#8a857a] mb-3">Contents</p>
      {sections.map((s, i) => (
        <div key={s.cat.id} className="flex justify-between border-t border-[#e6dfcc] py-2 text-[13px] text-[#14130f]">
          <span><span className="font-mono-j text-[10px] text-[#9a7b26] mr-4">{String(i + 1).padStart(2, "0")}</span>{s.cat.name}</span>
          <span className="text-[#8a857a]">{s.items.length} items</span>
        </div>
      ))}
      <p className="mt-6 text-[10px] text-[#8a857a]">Printed {new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</p>
    </div>
  </section>
);

export default function PrintPage() {
  const [params, setParams] = useSearchParams();
  const sheetZoom = useSheetZoom();
  const shared = params.get("items");
  const isSel = params.get("selection") === "1" || !!shared;
  const shortlist = useShortlist();
  const selIds = shared ? shared.split(",") : shortlist.ids;
  const selMeta = shared ? { client: params.get("client") || "", project: params.get("project") || "" } : shortlist;
  const supplier = params.get("supplier") || "";
  const style = params.get("style") || "";
  const finish = params.get("finish") || "";
  const sel = isSel ? "selection" : params.get("cat") || "all";
  const { data: catData } = useSWR("/categories", fetcher);
  const { data: itemData } = useSWR("/items", fetcher);
  const cats = asList(catData);
  const items = asList(itemData);
  const scoped = items.filter((i) => (sel === "all" || sel === "selection" || i.category === sel) && (!isSel || selIds.includes(i.id)));
  const suppliers = [...new Set(scoped.map((i) => i.supplier).filter(Boolean))].sort();
  const styles = [...new Set(scoped.map((i) => i.style).filter(Boolean))].sort((a, b) => (((STYLES.indexOf(a) + 1) || 99) - ((STYLES.indexOf(b) + 1) || 99)) || a.localeCompare(b));
  const shown = scoped.filter((i) => (!supplier || i.supplier === supplier) && (!style || i.style === style) && (!finish || itemFinishes(i).includes(finish)));
  const sections = cats
    .map((cat) => ({ cat, items: shown.filter((i) => i.category === cat.slug) }))
    .filter((s) => s.items.length > 0);
  const breakUp = sel === "all";
  const shots = breakUp ? projectShots(items.filter((i) => i.category === "projects")) : [];
  const pages = sections.flatMap((s, si) => {
    const groups = breakUp && s.cat.slug !== "projects" ? groupBySupplier(s.items) : [{ sup: "", items: s.items }];
    return groups.map((g, gi) => ({ key: `${s.cat.id}-${gi}`, s, si, g, first: gi === 0 }));
  });
  let shotIdx = 0;
  const setFilter = (k, v) => {
    const p = new URLSearchParams(params);
    if (v && v !== "any") p.set(k, v); else p.delete(k);
    setParams(p);
  };
  const filterSel = (k, value, opts, all) => (
    <Select value={value || "any"} onValueChange={(v) => setFilter(k, v)}>
      <SelectTrigger data-testid={`print-${k}-select`} className="h-11 w-40 shrink-0 rounded-full bg-[#13141C] border-[#D4AF37]/30 text-[#F5F3EF]"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#13141C] border-[#D4AF37]/30 text-[#F5F3EF]">
        <SelectItem value="any">{all}</SelectItem>
        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="print-root min-h-screen bg-[#0D0D11]">
      <div className="no-print sticky top-0 z-40 bg-[#0D0D11]/90 backdrop-blur-xl border-b border-[#D4AF37]/15">
        <div className="mx-auto max-w-[900px] px-4 sm:px-5 py-3 lg:py-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="flex items-center justify-between gap-3">
            <Link to="/" data-testid="print-back-link" className="inline-flex items-center gap-2 text-sm whitespace-nowrap text-[#A09E97] hover:text-[#E5C158]">
              <ArrowLeft className="w-4 h-4" /> Back to Atlas
            </Link>
            <button onClick={() => window.print()} data-testid="print-now-btn-mobile"
              className="lg:hidden h-10 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors">
              <Printer className="w-4 h-4" /> Print<span className="hidden sm:inline"> / Save PDF</span>
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto -mx-4 px-4 pb-1 sm:-mx-5 sm:px-5 lg:pb-0 lg:mx-0 lg:px-0 lg:overflow-visible lg:flex-wrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="print-filter-row">
            {(suppliers.length > 1 || supplier) && filterSel("supplier", supplier, suppliers, "All suppliers")}
            {(styles.length > 1 || style) && filterSel("style", style, styles, "All styles")}
            <Select value={sel} onValueChange={(v) => setParams(v === "all" ? {} : v === "selection" ? { selection: "1" } : { cat: v })}>
              <SelectTrigger data-testid="print-category-select" className="h-11 w-48 shrink-0 rounded-full bg-[#13141C] border-[#D4AF37]/30 text-[#F5F3EF]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#13141C] border-[#D4AF37]/30 text-[#F5F3EF]">
                <SelectItem value="all">Full catalogue</SelectItem>
                <SelectItem value="selection">{shared ? "Shared selection" : `My selection (${shortlist.ids.length})`}</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <button onClick={() => window.print()} data-testid="print-now-btn"
              className="hidden lg:inline-flex h-11 items-center gap-2 rounded-full bg-[#D4AF37] px-6 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors shrink-0">
              <Printer className="w-4 h-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>
      <div className="sheets py-6 sm:py-10 space-y-6 sm:space-y-10" style={sheetZoom} data-testid="print-sheets">
        <Cover sections={sections} single={sel !== "all"} selection={isSel ? selMeta : null} filters={[supplier, style, finish && `${finish} finish`].filter(Boolean).join(" · ")} />
        {pages.map(({ key, s, si, g, first }, pi) => {
          const shot = pi > 0 && s.cat.slug !== "projects" && shots.length ? shots[shotIdx++ % shots.length] : null;
          return [
            shot && <PrintInterlude key={`${key}-i`} shot={shot} n={pi} next={[s.cat.name, g.sup].filter(Boolean).join(" · ")} />,
            <section key={key} className="sheet" data-testid={`print-section-${s.cat.slug}${first ? "" : `-${g.sup.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}`}>
              <header className="flex items-end justify-between border-b border-[#9a7b26] pb-3 mb-6">
                <div>
                  <p className="font-mono-j text-[9px] tracking-[0.3em] uppercase text-[#9a7b26]">Section {String(si + 1).padStart(2, "0")}{g.sup && ` · ${g.sup}`}</p>
                  <h2 className="font-display text-[38px] leading-none text-[#14130f] mt-1">{first ? s.cat.name : g.sup}</h2>
                </div>
                <span className="text-[10px] text-[#8a857a]">{first ? s.cat.description : s.cat.name}</span>
              </header>
              <div className="grid grid-cols-3 gap-4">
                {g.items.map((it) => <PrintCard key={it.id} item={it} />)}
              </div>
            </section>,
          ];
        })}
        <PrintBackCover />
      </div>
    </div>
  );
}
